/**
 * customer-cards.tsx — Danh sách khách hàng dạng THẺ cho điện thoại (kiểu danh bạ Lark Mobile), thay cho bảng nhiều cột:
 * ảnh tròn · tên đậm · số điện thoại · chip giai đoạn · người phụ trách / điểm · nút gọi nhanh (tel:).
 * Bấm thẻ mở hồ sơ khách. Hàng chip giai đoạn (cuộn ngang) thay cho ô chọn.
 */
import { StageBadge } from '@/components/shared/stage-badge'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import dayjs from 'dayjs'
import { ChevronRight, Phone } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/shared/feedback'
import { LIFECYCLE_STAGES, STAGE_LABELS, stageBadgeVariant, stageLabel, type ContactListItem } from '@/hooks/use-contacts'
import { cn, initials } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

/** Số điện thoại → liên kết gọi (`tel:`): bỏ khoảng trắng / dấu chấm, giữ + đầu. */
export function telHref(phone: string | null | undefined): string | null {
  const digits = (phone ?? '').replace(/[^\d+]/g, '')
  return /\d{6,}/.test(digits) ? `tel:${digits}` : null
}

export function StageChips({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('custDetail')
  const { stages } = useLifecycleStages()
  const items = [{ id: '__all__', label: t('cards.all') }, ...stages.map((s) => ({ id: s.key, label: s.label }))]
  return (
    <div role="tablist" aria-label={t('cards.stageAria')} className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((it) => {
        const active = value === it.id
        return (
          <button
            key={it.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.id)}
            className={cn(
              'h-8 shrink-0 rounded-full px-3.5 text-[13px] transition-colors',
              active ? 'bg-primary font-medium text-primary-foreground' : 'bg-secondary text-secondary-foreground active:bg-secondary/70',
            )}
          >
            {it.label}
          </button>
        )
      })}
    </div>
  )
}

export function CustomerCards({ rows, loading, emptyText, onOpen }: {
  rows: ContactListItem[]
  loading: boolean
  emptyText: string
  onOpen: (c: ContactListItem) => void
}) {
  const { t } = useTranslation('custDetail')
  if (loading) return <Loading label={t('cards.loading')} />
  if (rows.length === 0) return <p className="py-10 text-center text-sm text-muted-foreground">{emptyText}</p>
  return (
    <ul className="space-y-2.5">
      {rows.map((c) => {
        const name = c.crmName || c.fullName || t('cards.noName')
        const tel = telHref(c.phone)
        return (
          <li key={c.id} className="flex items-center gap-3 rounded-2xl border bg-card p-3 shadow-sm">
            <button type="button" onClick={() => onOpen(c)} className="flex min-w-0 flex-1 items-center gap-3 text-left active:opacity-70">
              <Avatar className="h-12 w-12 shrink-0">
                {c.avatarUrl && <AvatarImage src={c.avatarUrl} alt={name} />}
                <AvatarFallback>{initials(name)}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[15px] font-semibold">{name}</span>
                </span>
                <span className="mt-0.5 block truncate text-sm tabular-nums text-muted-foreground">{c.phone || c.email || t('cards.noContact')}</span>
                <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <StageBadge stage={c.lifecycleStage} />
                  <span className="truncate text-xs text-muted-foreground">
                    {c.assignedUser?.fullName ? t('cards.assignedTo', { name: c.assignedUser.fullName }) : t('cards.createdOn', { date: dayjs(c.createdAt).format('DD/MM/YYYY') })}
                  </span>
                </span>
              </span>
            </button>
            {/* Có số điện thoại → nút gọi nhanh; không có → mũi tên gợi ý bấm vào thẻ mở hồ sơ. */}
            {tel ? (
              <a href={tel} aria-label={t('cards.callAria', { name })} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success/10 text-success active:bg-success/20">
                <Phone className="h-[18px] w-[18px]" />
              </a>
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            )}
          </li>
        )
      })}
    </ul>
  )
}
